import { Portal } from '@reach/portal';
import cl from 'classnames';
import { motion } from 'framer-motion';
import * as React from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';

import { LANDING_HEADER_HEIGHT } from '../Layout/Landing/constants';
import { DEFAULT_Z_INDEX, MotionDrawerProps, PLACEMENT } from './MotionDrawer.types';

const hiddenTransition = {
  zIndex: -1,
};

const Drawer = styled(motion.section)<{ placement: MotionDrawerProps['placement'] }>`
  ${variant({
    prop: 'placement',
    variants: {
      [PLACEMENT.TOP]: {
        width: '100%',
        top: 0,
        left: 0,
      },
      [PLACEMENT.BOTTOM]: {
        width: '100%',
        bottom: 0,
        left: 0,
      },
      [PLACEMENT.LEFT]: {
        height: '100%',
        top: 0,
        left: 0,
      },
      [PLACEMENT.RIGHT]: {
        height: '100%',
        top: 0,
        right: 0,
      },
    },
  })}
`;

export const MotionDrawer = (props: MotionDrawerProps) => {
  const {
    children,
    className,
    visible = false,
    zIndex = DEFAULT_Z_INDEX,
    onClose,
    parentNavEl,
    placement = PLACEMENT.LEFT,
    transparent = false,
  } = props;
  const animateValue = visible ? 'visible' : 'hidden';
  const placementVariant = React.useMemo(
    () => ({
      [PLACEMENT.TOP]: {
        hidden: {
          y: '-100%',
          transitionEnd: hiddenTransition,
        },
        visible: {
          y: (parentNavEl?.getBoundingClientRect()?.y || 0) + LANDING_HEADER_HEIGHT,
          zIndex,
        },
      },
      [PLACEMENT.BOTTOM]: {
        hidden: {
          y: '200%',
          transitionEnd: hiddenTransition,
        },
        visible: {
          y: -parentNavEl?.offsetHeight || 0,
          zIndex,
        },
      },
      [PLACEMENT.LEFT]: {
        hidden: {
          x: '-100%',
          transitionEnd: hiddenTransition,
        },
        visible: {
          x: parentNavEl?.offsetWidth || 0,
          zIndex,
        },
      },
      [PLACEMENT.RIGHT]: {
        hidden: {
          x: '200%',
          transitionEnd: hiddenTransition,
        },
        visible: {
          x: -parentNavEl?.offsetWidth || 0,
          zIndex,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentNavEl?.getBoundingClientRect()?.y, parentNavEl?.offsetHeight, zIndex]
  );

  const overlayVariant = React.useMemo(
    () => ({
      hidden: {
        opacity: 0,
        transitionEnd: hiddenTransition,
      },
      visible: {
        opacity: 1,
        zIndex: zIndex - 1,
      },
    }),
    [zIndex]
  );

  const transition = React.useMemo(() => ({ type: 'tween' }), []);

  return (
    <Portal>
      <Drawer
        className={cl('drawer', className, { transparent: transparent })}
        initial="hidden"
        animate={animateValue}
        variants={placementVariant[placement]}
        transition={transition}
        placement={placement}
      >
        {children}
      </Drawer>
      <motion.div
        className={'overlay'}
        initial="hidden"
        animate={animateValue}
        variants={overlayVariant}
        transition={transition}
        onClick={onClose}
      />
    </Portal>
  );
};
